import {Component, Prop, h, Host, Event, EventEmitter, State, Element, Watch} from '@stencil/core'
import { ICON_TYPES, VARIANTS_TYPES, InputFieldsInterface } from '../../utils'
import { DropdownMenuItem } from '../../utils/interfaces'

@Component({
  tag: 'snap-input',
  styleUrl: 'snap-input.scss',
})

export class SnapInput implements InputFieldsInterface {
  @Prop({ reflect: true }) textarea: boolean = false

  @Prop({ reflect: true }) maxlength: string

  @Prop({ reflect: true, attribute: 'charCount' }) displayCharCount: boolean = false

  /** Used for input inline styling */
  @Prop({ reflect: true }) basic: boolean = false

  /** Choose from selection of hero icons */
  @Prop({ reflect: true }) icon: ICON_TYPES

  /** Positions icon either left or right of input */
  @Prop({ reflect: true }) iconPosition: 'left' | 'right'

  /** Creates label above input wrapper */
  @Prop() label: string

  /** Create text before input */
  @Prop() _prepend: string

  /** Create text after input */
  @Prop() _append: string

  /** Name the field. Used in 'label for=' and input name */
  @Prop() name: InputFieldsInterface['name']

  /** Show label above the field or not */
  @Prop({ reflect: true }) showLabel: InputFieldsInterface['showLabel'] = true

  /** Id for the field */
  @Prop({ reflect: true }) _id!: InputFieldsInterface['_id']

  @Prop({ reflect: true }) value: InputFieldsInterface['value']

  /** What _type of field is this */
  @Prop({ mutable: true }) _type: InputFieldsInterface['_type'] = 'text'

  /** The HTML autocomplete attribute lets web developers specify what if any
   * permission the user agent has to provide automated assistance in filling
   * out form field values, as well as guidance to the browser as to the _type of
   * information expected in the field.
   */
  @Prop() autocomplete: InputFieldsInterface['autocomplete'] = 'off'

  /** Text shown in the field before the user _types */
  @Prop() placeholder: InputFieldsInterface['placeholder']

  /** A hint in the upper right next to the label */
  @Prop() cornerHint: InputFieldsInterface['cornerHint']

  /** Help text under the field */
  @Prop() helpText: InputFieldsInterface['helpText']

  /** For testing purposes */
  @Prop() testId: InputFieldsInterface['testId']

  /** If the field is to be validated, pass this prop with true on error */
  @Prop({ mutable: true }) error: InputFieldsInterface['error']

  @Prop({ mutable: true }) errorIcon: boolean = false

  @Prop({ mutable: true }) success: boolean = false

  @Prop({ mutable: true }) successIcon: boolean = false

  @Prop() showPasswordBtn: boolean = false

  @Prop() clearInputBtn = false

  @Prop() inputBtn: string

  @Prop({ reflect: true }) inputBtnVariant: VARIANTS_TYPES = 'tertiary'

  @Prop( { reflect: true }) inputBtnDisabled: boolean = false

  @Prop( { reflect: true }) inputBtnIcon: ICON_TYPES

  @Prop() inputSelectButton: string

  @Prop() inputSelectOptions: DropdownMenuItem[]

  @Prop({ reflect: true }) inputSelectPosition: 'left' | 'right'

  @Prop( {reflect: true}) disabled: boolean = false

  @Prop( {reflect: true}) locked: boolean = false

  @Prop({ reflect: true }) mask: boolean = false

  @Prop() submitBtnIcon: ICON_TYPES

  @Prop({ reflect: true }) required: boolean = false

  @Prop({ reflect: true }) date: boolean = false

  /** Text to show on validation error */

  @State() localValue: InputFieldsInterface['value']

  @State() hasCodeSlot: boolean = false

  @State() isPasswordShown: boolean = false

  @State() currentLength: string = '0'

  @State() isPhoneWithMaskValid = false

  @Element() private element: HTMLElement

  @Event({ eventName: 'snap-input-blur', bubbles: true, composed: true }) snapInputBlur: EventEmitter<FocusEvent>

  @Event({ eventName: 'snap-input-focus', bubbles: true, composed: true }) snapInputFocus: EventEmitter<FocusEvent>

  @Event({ eventName: 'snap-input-change', bubbles: true, composed: true }) snapInputChange: EventEmitter

  @Event({ eventName: 'snap-input-date-change', bubbles: true, composed: true }) snapInputDateChange: EventEmitter<number>

  @Event({ eventName: 'input-btn-click' }) inputBtnClick: EventEmitter

  @Event({ eventName: 'input', bubbles: true, composed: true }) input: EventEmitter

  private inputField: HTMLInputElement
  private textareaField: HTMLTextAreaElement
  private trailingBtn: HTMLSnapButtonElement
  private MASK: string = '(___) ___-____'
  private previousInputValue = null
  private nonNumericSymbol = false

  @Watch('value')
  onValueUpdate() {
    this.localValue = this.value

    if (this._type === 'tel' && this.mask && !this.textarea) {
      if (this.value?.length === 10) {
        this.localValue = `(${this.value[0]}${this.value[1]}${this.value[2]}) ${this.value[3]}${this.value[4]}${this.value[5]}-${this.value[6]}${this.value[7]}${this.value[8]}${this.value[9]}`
        this.inputField?.classList.remove('hideColor')
      }
      else {
        this.localValue = this.MASK
        this.inputField?.classList.add('hideColor')
      }
    }
    else if (this.date && this.value) {
      this.inputField.value = this.value
    }

  }

  componentWillLoad() {
    if (Array.from(this.element.children).length) {
      this.hasCodeSlot = !!this.element.querySelector('[slot="code"]')
    }
  }

  componentDidLoad() {
    const field = this.textareaField ? this.textareaField : this.inputField

    field?.addEventListener('input', event => {
      const target = event.currentTarget
      const currentLength = (target as HTMLTextAreaElement | HTMLInputElement).value.length
      this.currentLength = currentLength.toString()
    })

    this.addRightPaddingToInput()
    this.onValueUpdate()
    this.checkIfDateInput()
  }

  checkIfDateInput() {
    if (this.date) {
      this.inputField.placeholder = 'MM/DD/YYYY'
    }
  }

  getInputContainer() {
    const classes = []

    if (this.showLabel) classes.push('input-container')
    if (classes.length === 0) return

    if (this.iconPosition === 'left') classes.push('left')

    if (this.inputSelectButton && this.inputSelectOptions && this.inputSelectPosition === 'left') classes.push('left')

    if (this._append || this._prepend) classes.push('addon')

    if (this._append || (this._prepend && this.basic)) {
      classes.push('basic')
    } else if (this._append || (this._prepend && !this.basic)) {
      classes.push('default')
    }

    if (this.success) classes.push('success')

    if (this.error) classes.push('error')

    if (this._append && this._prepend) classes.push('combo')

    if (this._type === 'password' && !!this.showPasswordBtn) classes.push('password')

    if (this.isPasswordShown) classes.push('password-show')

    if (this.clearInputBtn) classes.push('clear-input')

    if (this.disabled) classes.push('disabled')

    if (this.locked) classes.push('locked')

    return classes.join(' ').toString()
  }

  renderHelpText() {
    return this.helpText && <p>{this.helpText}</p>
  }

  addRightPaddingToInput() {
    if (this.trailingBtn) {
      setTimeout( () => {
        const inputRightPadding = this.trailingBtn.offsetWidth
        const deltaPaddingRight = 12
        this.inputField.style.paddingRight = `${inputRightPadding + deltaPaddingRight}px`
      }, 100)
    }
  }

  renderCounter() {
    return (
      this.displayCharCount &&
      (
        <div>
          <span class="current-length">{this.currentLength}</span>
          {this.maxlength && this.maxlength !== 'undefined' && <span class="max-length">/{this.maxlength}</span>}
        </div>
      )
    )
  }

  renderHelpTextCounter() {
    if ((this.displayCharCount && this.textarea) || this.helpText) {
      return (
        <div class="help-text">
          {this.renderHelpText()}
          {this.renderCounter()}
        </div>
      )
    }
  }

  onPasswordBtnClick = () => {
    this.inputField.type !== 'text' ? (this.inputField.type = 'text') : (this.inputField.type = 'password')
    this.isPasswordShown = !this.isPasswordShown
  }

  onClickClearInput = () => {
    this.inputField.value = ''
  }

  onInputBtnClick = () => {
    this.inputBtnClick.emit()
  }

  onChangeHandler = (e) => {
    this.snapInputChange.emit(e)
  }

  onInputHandler = (e) => {
    if (this.nonNumericSymbol) {
      e.preventDefault()
      this.nonNumericSymbol = false
      return
    }

    const inputValue = e.target.value

    if (e.target.value.length > 10) {
      this.inputField.value = e.target.value.slice(0, -1);
      return
    }


    if (inputValue.length === 2 || inputValue.length === 5) {
      if (inputValue.length > this.previousInputValue.length) {
        e.target.value = inputValue + '/';
      }
    }

    if (inputValue.length === 3) {
      if (inputValue[2] !== '/') {
        this.inputField.value = e.target.value.slice(0, 2) + '/' + e.target.value.slice(2);
      }
    }
    else if (inputValue.length === 6) {
      if (inputValue[5] !== '/') {
        this.inputField.value = e.target.value.slice(0, 5) + '/' + e.target.value.slice(5);
      }
    }

    if (e.target.value.length === 10) {
      this.checkIsValidDate(this.inputField.value)
    }
    else {
      this.success = false
      this.successIcon = false
      this.error = false
      this.errorIcon = false
    }

    this.previousInputValue = e.target.value

  }

  checkIsValidDate = (date: string) => {
    const [month, day, year] = date.split('/');
    const isValidDate = this.isValidMonthDayYear(month, day, year);

    if (isValidDate) {
      this.snapInputDateChange.emit(Date.parse(date))
    }
    else {
      this.error = true
      this.errorIcon = true
    }
  }

  isValidMonthDayYear = (month, day, year) => {
    const maxDayOfMonth = new Date(year, month, 0).getDate();
    return day >= 1 && day <= maxDayOfMonth && month >= 1 && month <= 12;
  }

  onKeyDownHandler = (e) => {
    // For input with 'date' property only
    if (this.date) {
      const allowedKeyCodes = [8] // Backspace
      if (!allowedKeyCodes.includes(e.keyCode) && (e.key < '0' || e.key > '9')) {
        e.preventDefault()
        this.nonNumericSymbol = true
      }
      return
    }

    const selection = e.target.selectionStart
    let newStr = e.target.value

    if (selection === this.MASK.length && (e.key !== 'ArrowLeft' && e.key !== 'Backspace')) {
      e.preventDefault()
      return
    }
    else if (e.key === 'ArrowLeft') {
      e.target.setSelectionRange(selection, selection)
    }
    else if (e.key === 'ArrowRight') {
      e.target.setSelectionRange(selection, selection)
    }
    else if (e.key === 'Backspace') {
      if (selection === 10) {
        newStr = newStr.substring(0, selection - 1) + '-' + newStr.substring(selection)
        this.inputField.value  = newStr
        this.localValue = newStr
      }
      else if (selection === 2 ||
        selection === 3 ||
        selection === 4 ||
        selection === 7 ||
        selection === 8 ||
        selection === 9 ||
        selection > 10 && selection <= this.MASK.length
      ) {
        newStr = newStr.substring(0, selection - 1) + '_' + newStr.substring(selection)
        this.inputField.value  = newStr
        this.localValue = newStr
      }

      if (selection !== 0) {
        e.target.setSelectionRange(selection - 1, selection - 1)
      }
      e.preventDefault()

      this.isPhoneWithMaskValid = this.checkPhoneMask() === 10;
      this.input.emit({value: newStr, valid: this.isPhoneWithMaskValid})
    }
    else if (isNaN(e.key) || e.key === ' ') {
      e.preventDefault()
      return
    }
    else if (selection === 4) {
      newStr = newStr.substring(0, selection + 2) + e.key + newStr.substring(selection + 3)
      this.inputField.value  = newStr
      this.localValue = newStr
      e.target.setSelectionRange(selection + 3, selection + 3)
      e.preventDefault()

      this.isPhoneWithMaskValid = this.checkPhoneMask() === 10;
      this.input.emit({value: newStr, valid: this.isPhoneWithMaskValid})
    }
    else if (selection === 5 || selection === 0 || selection === 9) {
      newStr = newStr.substring(0, selection + 1) + e.key + newStr.substring(selection + 2)
      this.inputField.value  = newStr
      this.localValue = newStr
      e.target.setSelectionRange(selection + 2, selection + 2)
      e.preventDefault()

      this.isPhoneWithMaskValid = this.checkPhoneMask() === 10;
      this.input.emit({value: newStr, valid: this.isPhoneWithMaskValid})
    }
    else if (selection === 1 ||
      selection === 2 ||
      selection === 3 ||
      selection === 6 ||
      selection === 7 ||
      selection === 8 ||
      selection >= 10 && selection <= this.MASK.length
    ) {
      newStr = newStr.substring(0, selection) + e.key + newStr.substring(selection + 1)
      this.inputField.value  = newStr
      this.localValue = newStr
      e.target.setSelectionRange(selection + 1, selection + 1)
      e.preventDefault()

      this.isPhoneWithMaskValid = this.checkPhoneMask() === 10;
      this.input.emit({e, value: newStr, valid: this.isPhoneWithMaskValid})
    }
    this.snapInputChange.emit(e)
  }

  onFocusHandler = (e) => {
    if (this._type === 'tel' && this.mask && !this.textarea) {
      if (this.checkPhoneMask() != 10) {
        setTimeout(() => {
          e.target.classList.remove('hideColor')
        })
      }
    }

    this.snapInputFocus.emit(e)
  }

  onBlurHandler = (e) => {
    if (this._type === 'tel' && this.mask && !this.textarea) {
      if (this.checkPhoneMask() != 10) {
        this.localValue = this.MASK
        e.target.value = this.localValue
        e.target.classList.add('hideColor')
      }
    }

    this.snapInputBlur.emit(e)
  }

  checkPhoneMask = (): number | undefined => {
    return this.localValue.match(/[0-9]/g)?.length
  }

  onInputTextAreaHandler = (e) => {
    this.localValue = e.target.value
    e.target.style.height = 'auto'
    e.target.style.height = `${e.target.scrollHeight}px`
  }

  renderInput() {
    return (
      <div class={this.getInputContainer()}>
        {this.label && !this.showLabel ? (
          <label htmlfor={this.label} class="sr-only">
            {this.label}
          </label>
        ) : null}
        {/* input wrapper */}
        {this.icon ? <snap-icon icon={this.icon} /> : null}
        {this._prepend && this.basic ? (
          <div class="prepend">
            <span class="basic-span">{this._prepend}</span>
          </div>
        ) : null}
        {this._prepend && !this.basic ? <span class="prepend">{this._prepend}</span> : null}

        <slot />

        {this.textarea ? (
          <textarea
            id={this._id}
            class={`snap-input-textarea ${this.submitBtnIcon ? 'snap-input-textarea-submit' : ''}`}
            rows={this.submitBtnIcon ? 1 : 4}
            value={this.localValue}
            name={this.name}
            placeholder={this.placeholder}
            maxlength={this.maxlength ? this.maxlength : null}
            data-testid={this.testId}
            onBlur={e => this.onBlurHandler(e)}
            onFocus={e => this.onFocusHandler(e)}
            onChange={e => this.onChangeHandler(e)}
            onInput={e => this.submitBtnIcon ? this.onInputTextAreaHandler(e) : undefined}
            ref={el => (this.textareaField = el as HTMLTextAreaElement)}
          />
        ) : (
          <input
            type={this._type}
            name={this.name}
            id={this._id}
            value={this.localValue}
            class={'snap-input'}
            placeholder={this.placeholder}
            autocomplete={this.autocomplete}
            maxlength={this.maxlength ? this.maxlength : null}
            disabled={this.disabled || this.locked}
            onBlur={e => this.onBlurHandler(e)}
            onFocus={e => this.onFocusHandler(e)}
            onChange={e => this.onChangeHandler(e)}
            onInput={e => this.date ? this.onInputHandler(e) : undefined}
            onKeyDown={e => ((this.mask && this._type === 'tel') || this.date) && !this.textarea ? this.onKeyDownHandler(e) : undefined}
            ref={el => (this.inputField = el as HTMLInputElement)}
            data-testid={this.testId}
            pattern={this.date ? '[0-9]*' : undefined}
          />
        )}
        {this.submitBtnIcon &&
          <button type="submit" class={`snap-input-textarea-btn-container ${this.localValue ? 'show' : ''}`}>
            <snap-button class="snap-input-textarea-btn" icon={this.submitBtnIcon} size="sm" buttonType="submit" variant="primary" />
          </button>
        }
        {this._append && !this.basic ? <span class="append">{this._append}</span> : null}
        {this._append && this.basic ? (
          <div class="append">
            <span class="basic-span">{this._append}</span>
          </div>
        ) : null}

        {this.error && this.errorIcon && <snap-icon icon="exclamation-circle-solid" class="info-icon" size={'sm'} />}

        {this.success && this.successIcon && <snap-icon icon="check-circle-solid" class="info-icon" size={'sm'} />}

        {this.date && (!this.error && !this.success && !this.locked) ? <snap-icon icon={'calendar-solid'} size={'sm'} /> : null}

        {this.date && this.locked ? <snap-icon icon={'lock-closed-solid'} size={'sm'} /> : null}

        {this._type === 'password' && this.showPasswordBtn && (
          <button class="password-btn" type="button" onClick={this.onPasswordBtnClick}>
            <snap-icon class="password-icon" icon="eye-solid" color="#475569" size="sm"></snap-icon>
          </button>
        )}
        {this.clearInputBtn && (
          <button type="button" class="clear-input-btn" onClick={this.onClickClearInput}>
            <snap-icon size="sm" icon="x-solid" />
          </button>
        )}

        {(this.inputBtn || this.inputBtnIcon) && this.inputBtnClick && (
          <snap-button
            size="md"
            class="input-btn"
            input-btn="true"
            icon={this.inputBtnIcon ? this.inputBtnIcon : undefined}
            disabled={this.inputBtnDisabled}
            variant={this.inputBtnVariant}
            onClick={this.onInputBtnClick}
            ref={el => (this.trailingBtn = el as HTMLSnapButtonElement)}
          >
            {this.inputBtn}
          </snap-button>
        )}

        {this.inputSelectButton && this.inputSelectOptions && this.inputSelectOptions.length > 0 && (
          <div class="input-select-class">
            <snap-dropdown
              class="input-select"
              button-text={this.inputSelectButton}
              size="lg"
              max-height="400px"
              left-hang={this.inputSelectPosition === 'left' && true}
              options={this.inputSelectOptions}></snap-dropdown>
          </div>
        )}
      </div>
    )
  }

  render() {
    return (
      <Host>
        {this.label && this.showLabel ? (
          <div>
            {/* label wrapper */}
            {this.label && !this.cornerHint && (
              <label htmlfor={this.label} class="label">
                { this.label }
                { this.required && <span class="required">*</span> }
              </label>
            )}
            {this.label && this.cornerHint && (
              <div class="corner-hint">
                <label htmlFor={this.name} class="label">
                  { this.label }
                  { this.required && <span class="required">*</span> }
                </label>
                <span class="basic-span" id={`${this.label}-${this.cornerHint}`}>
                  { this.cornerHint }
                </span>
              </div>
            )}
            {this.renderInput()}
            {this.renderHelpTextCounter()}
            {this.hasCodeSlot && (
              <div class="snap-auth-input-label-helper-link">
                <slot name="code" />
              </div>
            )}
          </div>
        ) : (
          this.renderInput()
        )}
      </Host>
    )
  }
}
